import { useId, type ReactNode } from 'react';
import { Button, Panel, PanelBody, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * The pieces the two self-contained catalogue cards share.
 *
 * `CatalogPage` has its own `CardShell`, `StatusBadge`, `ErrorBlock` and
 * `InfoIcon`, and they are module-private there. Rather than export them out of
 * a 2,900-line page — or, worse, let two new cards each keep a private copy that
 * agrees on the day it is written and drifts on the first restyle — the shapes
 * are declared once here and the page keeps its own for the fourteen registry
 * masters it renders itself.
 *
 * ⚠️ Everything exported from this file is a COMPONENT. `adminWriteDenial` is a
 * plain function and lives in `admin-write.ts` next door, because
 * `react/only-export-components` is on and a mixed module breaks fast refresh
 * for whatever else is in it.
 */

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function CatalogInfoIcon() {
  return (
    <svg {...stroke} className="mt-px h-4 w-4 shrink-0">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

/**
 * A globe with a meridian band — a region of the world under one set of billing
 * rules.
 *
 * Deliberately NOT `CatalogPage`'s plain `GlobeIcon`, which marks Countries, and
 * not its `CompassIcon`, which marks Regions of focus. Three lists on one screen
 * are about geography and they answer three different questions; identical marks
 * would invite exactly the conflation that has already cost this codebase a
 * wrong join between `f_countries.region_id` and `f_regions`.
 */
export function BillingRegionIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="12" cy="12" r="9" />
      <path d="M3.6 9h16.8" />
      <path d="M3.6 15h16.8" />
      <path d="M12 3a15 15 0 0 1 0 18" />
      <path d="M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
}

/**
 * Two people under a bracket — a department a contact belongs to.
 *
 * Not `CatalogPage`'s `PeopleIcon`, which marks the Contacts card on the same
 * screen: a group is the label on a contact, not a contact.
 */
export function ContactGroupIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="8" cy="9" r="3" />
      <circle cx="16.5" cy="10" r="2.25" />
      <path d="M3 19v-1.5A3.5 3.5 0 0 1 6.5 14h3A3.5 3.5 0 0 1 13 17.5V19" />
      <path d="M15.5 14.5h1a3.5 3.5 0 0 1 3.5 3.5V19" />
    </svg>
  );
}

/**
 * Active vs retired, as a word plus a dot — never colour alone. Matches
 * `CatalogPage`'s own status exactly, because the two sit in the same grid.
 *
 * Plain text with a dot rather than a filled pill: nearly every row is Active,
 * and a column of identical green pills shouted the common case. The word is
 * still spelled out, so the dot is never the only signal.
 */
export function CatalogStatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? 'inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-fg-secondary'
          : 'inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-fg-muted'
      }
    >
      <span
        aria-hidden="true"
        className={active ? 'h-1.5 w-1.5 rounded-full bg-success' : 'h-1.5 w-1.5 rounded-full bg-fg-subtle'}
      />
      {active ? 'Active' : 'Retired'}
    </span>
  );
}

/**
 * A `danger`-toned block naming what failed. One of the three states every data
 * surface in this app owes a reader.
 */
export function CatalogErrorBlock({ children }: { children: ReactNode }) {
  return (
    <Notice tone="danger" role="alert">
      <span className="[overflow-wrap:anywhere]">{children}</span>
    </Notice>
  );
}

/**
 * A column an administrator has not filled in yet.
 *
 * ⚠️ **Words, never an em dash**, and the distinction is load-bearing on the
 * billing-region table specifically. An em dash in a Xero column would read as
 * "this failed to load"; every one of those columns is `null` on every row
 * today, deliberately — migration 120 seeded them that way because nobody had
 * the tenant's codes — so the normal reading of this whole screen on day one is
 * "nobody has set these yet", which is a sentence and not a dash.
 *
 * The same treatment `CatalogPage` gives a campaign inclusion with no unit.
 */
export function NotSet({ children = 'Not set' }: { children?: ReactNode }) {
  return <span className="text-fg-subtle">{children}</span>;
}

/**
 * The card in the Managed lists grid: icon, title, one line, some badges and a
 * Manage/View button.
 *
 * Shaped to match `CatalogPage`'s `CardShell` to the class name, because the two
 * render side by side in one grid and a card that is a few pixels different
 * reads as a mistake rather than as a distinction.
 *
 * The heading is an `h3`: the section heading above the grid is the `h2`.
 */
export function CatalogMasterCard({
  icon,
  title,
  blurb,
  loading,
  error,
  badges,
  canWrite,
  onOpen,
}: {
  icon: ReactNode;
  title: string;
  /**
   * One line under the title. Optional, and only ever a REFUSAL or a fact the
   * reader cannot otherwise see — the no-helper-text rule applies here exactly
   * as it does everywhere else.
   */
  blurb?: string;
  loading: boolean;
  error: string | null;
  /** Rendered only once the list has answered. */
  badges: ReactNode;
  canWrite: boolean;
  onOpen: () => void;
}) {
  const headingId = useId();
  return (
    <Panel className="min-w-0" aria-labelledby={headingId}>
      <PanelBody className="flex h-full flex-col gap-3">
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className="shrink-0 text-fg-subtle">
            {icon}
          </span>
          <div className="min-w-0">
            <h3 id={headingId} className="m-0 text-sm font-semibold text-fg">
              {title}
            </h3>
            {blurb && <p className="mt-0.5 text-xs text-fg-muted">{blurb}</p>}
          </div>
        </div>

        <div className="min-h-5">
          {loading ? (
            <Skeleton className="h-4 w-24" />
          ) : error ? (
            <span className="text-xs text-danger">Couldn’t load this list.</span>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">{badges}</div>
          )}
        </div>

        <div className="mt-auto">
          <Button variant="ghost" size="sm" onClick={onOpen}>
            <span className="sr-only">
              {canWrite ? `Manage ${title}` : `View ${title}`}
            </span>
            <span aria-hidden="true">{canWrite ? 'Manage' : 'View'}</span>
          </Button>
        </div>
      </PanelBody>
    </Panel>
  );
}

/**
 * The bordered note at the top of a list modal, saying how removal works on this
 * particular master.
 *
 * ⚠️ **Not helper text, and it must not be swept as such.** On both callers it
 * states the reason a control a reader is looking for is absent — no delete, or
 * no create/delete/retire at all — which is precisely the "why a control is
 * disabled" exception. A bare missing button is indistinguishable from a
 * feature somebody removed, and this screen has already had that reported once,
 * on the companies "+" menu.
 */
export function CatalogMasterNote({ children }: { children: ReactNode }) {
  return (
    <Notice tone="neutral">{children}</Notice>
  );
}
